import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import "./TextArea.scss";
import { isUrl } from "../../../utils/generalUtils";

const TextArea = ({
  data,
  formValidation = true,
  formClass,
  formatters = {},
  validators = {},
  onChange,
}) => {
  const [value, setValue] = useState(""); // Original value (what gets saved)
  const [displayValue, setDisplayValue] = useState(""); // Display value (what user sees)
  const [isValid, setIsValid] = useState(formValidation);
  const [errorMessage, setErrorMessage] = useState("");
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [multipleValues, setMultipleValues] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState("");
  const [currentRows, setCurrentRows] = useState(data?.rows || 3);

  const textareaRef = useRef(null);
  const editTextareaRef = useRef(null);

  // Memoized initial value calculation
  const initialValue = useMemo(() => {
    if (data?.answer?.value) {
      return data.answer.value;
    }
    if (data?.defaultValue) {
      return data.defaultValue;
    }
    return "";
  }, [data?.key, data?.answer?.value, data?.defaultValue]);

  // Enhanced input mask application with multiple mask support
  const applyInputMask = useCallback((inputValue, mask) => {
    if (!mask) return inputValue;

    // Handle multiple masks
    if (Array.isArray(mask)) {
      for (const singleMask of mask) {
        const masked = applyInputMask(inputValue, singleMask);
        if (masked.length === singleMask.length) {
          return masked;
        }
      }
      return inputValue;
    }

    let maskedValue = "";
    let valueIndex = 0;
    const cleanValue = inputValue;

    for (let i = 0; i < mask.length && valueIndex < cleanValue.length; i++) {
      const maskChar = mask[i];
      const inputChar = cleanValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
        if (inputChar === maskChar) {
          valueIndex++;
        }
      } else {
        valueIndex++;
        i--;
      }
    }

    return maskedValue;
  }, []);

  // Apply display mask - only affects display, doesn't change original value
  const applyDisplayMask = useCallback((inputValue, displayMask) => {
    if (!displayMask) return inputValue;

    // For textarea, we might want to apply masks line by line or to the entire content
    // This implementation applies the mask to the entire content as a single string
    if (Array.isArray(displayMask)) {
      for (const singleMask of displayMask) {
        const masked = applyDisplayMask(inputValue, singleMask);
        if (inputValue.length <= singleMask.replace(/[^9a*A]/g, "").length) {
          return masked;
        }
      }
      return applyDisplayMask(inputValue, displayMask[0]);
    }

    let maskedValue = "";
    let valueIndex = 0;

    for (
      let i = 0;
      i < displayMask.length && valueIndex < inputValue.length;
      i++
    ) {
      const maskChar = displayMask[i];
      const inputChar = inputValue[valueIndex];

      if (maskChar === "9" && /\d/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "a" && /[a-zA-Z]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "*" && /[a-zA-Z0-9]/.test(inputChar)) {
        maskedValue += inputChar;
        valueIndex++;
      } else if (maskChar === "A" && /[A-Z]/.test(inputChar.toUpperCase())) {
        maskedValue += inputChar.toUpperCase();
        valueIndex++;
      } else if (
        maskChar !== "9" &&
        maskChar !== "a" &&
        maskChar !== "*" &&
        maskChar !== "A"
      ) {
        maskedValue += maskChar;
      } else {
        break;
      }
    }

    return maskedValue;
  }, []);

  // Generic formatter application
  const applyFormatter = useCallback(
    (inputValue, formatterKey) => {
      const formatter = formatters[formatterKey];
      if (formatter && typeof formatter === "function") {
        return formatter(inputValue);
      }

      // Built-in formatters
      switch (formatterKey) {
        case "uppercase":
          return inputValue.toUpperCase();
        case "lowercase":
          return inputValue.toLowerCase();
        case "capitalize":
          return inputValue.replace(/\b\w/g, (l) => l.toUpperCase());
        case "trim":
          return inputValue.trim();
        case "truncateMultipleSpaces":
          return data?.truncateMultipleSpaces
            ? inputValue.replace(/\s+/g, " ")
            : inputValue;
        default:
          return inputValue;
      }
    },
    [formatters, data?.truncateMultipleSpaces]
  );

  // Generate dynamic error messages
  const generateErrorMessage = useCallback(
    (validationType, config, inputValue) => {
      const validation = data?.validate || {};

      // Return custom message if provided
      if (validation.customMessage) {
        return validation.customMessage;
      }

      // Generate dynamic messages based on validation type
      switch (validationType) {
        case "required":
          return data?.invalidMessage || "This field is required";
        case "minLength":
          return `Minimum ${config} characters required (current: ${inputValue.length})`;
        case "maxLength":
          return `Maximum ${config} characters allowed (current: ${inputValue.length})`;
        case "minWords":
          const currentWords = inputValue
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
          return `Minimum ${config} words required (current: ${currentWords})`;
        case "maxWords":
          const words = inputValue
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0).length;
          return `Maximum ${config} words allowed (current: ${words})`;
        case "pattern":
          return "Invalid format - please check your input";
        default:
          return "Invalid input";
      }
    },
    [data?.validate, data?.invalidMessage]
  );

  // Enhanced validation with dynamic error messages
  const validateInput = useCallback(
    (inputValue) => {
      const validation = data?.validate || {};
      const customValidator = validators[data?.key];

      // Custom validator takes precedence
      if (customValidator && typeof customValidator === "function") {
        return customValidator(inputValue, validation);
      }

      let isValid = true;
      let errorMessage = "";

      if (validation?.required && !inputValue.trim()) {
        isValid = false;
        errorMessage = generateErrorMessage("required", null, inputValue);
      } else if (inputValue.trim()) {
        // Length validation
        if (validation?.minLength && inputValue.length < validation.minLength) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "minLength",
            validation.minLength,
            inputValue
          );
        } else if (
          validation?.maxLength &&
          inputValue.length > validation.maxLength
        ) {
          isValid = false;
          errorMessage = generateErrorMessage(
            "maxLength",
            validation.maxLength,
            inputValue
          );
        } else {
          // Word count validation
          const words = inputValue
            .trim()
            .split(/\s+/)
            .filter((word) => word.length > 0);
          if (validation?.minWords && words.length < validation.minWords) {
            isValid = false;
            errorMessage = generateErrorMessage(
              "minWords",
              validation.minWords,
              inputValue
            );
          } else if (
            validation?.maxWords &&
            words.length > validation.maxWords
          ) {
            isValid = false;
            errorMessage = generateErrorMessage(
              "maxWords",
              validation.maxWords,
              inputValue
            );
          } else {
            // Pattern validation
            if (
              validation?.pattern &&
              !new RegExp(validation.pattern).test(inputValue)
            ) {
              isValid = false;
              errorMessage = generateErrorMessage(
                "pattern",
                validation.pattern,
                inputValue
              );
            }
          }
        }
      }

      return { isValid, errorMessage };
    },
    [
      data?.validate,
      data?.invalidMessage,
      data?.key,
      validators,
      generateErrorMessage,
    ]
  );

  // Update counts
  const updateCounts = useCallback((inputValue) => {
    setCharCount(inputValue.length);
    const words = inputValue
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    setWordCount(inputValue.trim() === "" ? 0 : words.length);
  }, []);

  // Auto-expand functionality
  const adjustTextareaHeight = useCallback(() => {
    if (data?.autoExpand && textareaRef.current) {
      const textarea = textareaRef.current;
      const minRows = data?.rows || 3;
      const maxRows = data?.maxRows || 10;

      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      
      // Calculate the number of lines
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const padding = 
        parseInt(getComputedStyle(textarea).paddingTop) +
        parseInt(getComputedStyle(textarea).paddingBottom);
      
      const lines = Math.max(
        minRows,
        Math.min(maxRows, Math.ceil((textarea.scrollHeight - padding) / lineHeight))
      );
      
      setCurrentRows(lines);
      textarea.style.height = `${lines * lineHeight + padding}px`;
    }
  }, [data?.autoExpand, data?.rows, data?.maxRows]);

  // Label positioning utilities
  const labelStyles = useMemo(() => {
    const position = data?.labelPosition || "top";
    const isHorizontal = [
      "left-left",
      "left-right",
      "right-left",
      "right-right",
    ].includes(position);

    const containerStyle = {
      top: {
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      bottom: {
        flexDirection: "column-reverse",
        justifyContent: "flex-start",
        alignItems: "stretch",
      },
      "left-left": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      },
      "left-right": {
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      },
      "right-left": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      },
      "right-right": {
        flexDirection: "row-reverse",
        justifyContent: "flex-end",
        alignItems: "flex-start",
      },
    }[position] || {
      flexDirection: "column",
      justifyContent: "flex-start",
      alignItems: "stretch",
    };

    const labelStyle = {
      width: isHorizontal && data?.labelWidth ? `${data.labelWidth}%` : "auto",
      textAlign: ["left-right", "right-right"].includes(position)
        ? "right"
        : "left",
      marginRight:
        ["left-left", "left-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      marginLeft:
        ["right-left", "right-right"].includes(position) && data?.labelMargin
          ? `${data.labelMargin}%`
          : "0",
      paddingTop: ["top", "bottom"].includes(position) ? "12px" : "0",
    };

    return { containerStyle, labelStyle };
  }, [data?.labelPosition, data?.labelWidth, data?.labelMargin]);

  // Handle multiple values for multiple textareas
  const handleMultipleAdd = useCallback(() => {
    if (value.trim()) {
      // Validate the current value before adding
      const validation = validateInput(value);
      if (validation.isValid) {
        const newValues = [...multipleValues, value.trim()];
        setMultipleValues(newValues);
        setValue("");
        setDisplayValue("");
        setIsValid(true);
        setErrorMessage("");
        // Reset textarea height
        if (data?.autoExpand) {
          setCurrentRows(data?.rows || 3);
        }
        // Call onChange with array of values for multiple textarea
        onChange?.(newValues);
      } else {
        // Show validation error
        setIsValid(false);
        setErrorMessage(validation.errorMessage);
      }
    }
  }, [value, multipleValues, onChange, validateInput, data?.autoExpand, data?.rows]);

  const handleMultipleRemove = useCallback(
    (index) => {
      if (
        multipleValues.length > 0 &&
        index >= 0 &&
        index < multipleValues.length
      ) {
        const newValues = multipleValues.filter((_, i) => i !== index);
        setMultipleValues(newValues);
        // Call onChange with updated array
        onChange?.(newValues);
      }
    },
    [multipleValues, onChange]
  );

  // Handle editing multiple values
  const handleMultipleEdit = useCallback(
    (index) => {
      if (index >= 0 && index < multipleValues.length) {
        setEditingIndex(index);
        setEditingValue(multipleValues[index]);
      }
    },
    [multipleValues]
  );

  const handleMultipleSave = useCallback(
    (index) => {
      if (editingValue.trim() && index >= 0 && index < multipleValues.length) {
        // Validate the edited value
        const validation = validateInput(editingValue.trim());
        if (validation.isValid) {
          const newValues = [...multipleValues];
          newValues[index] = editingValue.trim();
          setMultipleValues(newValues);
          setEditingIndex(-1);
          setEditingValue("");
          onChange?.(newValues);
        }
      }
    },
    [editingValue, multipleValues, onChange, validateInput]
  );

  const handleMultipleCancel = useCallback(() => {
    setEditingIndex(-1);
    setEditingValue("");
  }, []);

  // Event handlers
  const handleFocus = useCallback((e) => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);

      let finalValue = data?.displayMask ? value : e.target.value;

      // Apply input mask on blur if specified
      if (data?.inputMask && data?.applyMaskOn === "blur") {
        finalValue = applyInputMask(finalValue, data.inputMask);
        setValue(finalValue);
        setDisplayValue(finalValue);
      }
      // Apply display mask on blur if specified
      else if (data?.displayMask && data?.applyMaskOn === "blur") {
        const displayMaskedValue = applyDisplayMask(
          finalValue,
          data.displayMask
        );
        setDisplayValue(displayMaskedValue);
        // Keep original value clean for saving
        setValue(finalValue);
      }

      // Apply formatter on blur
      if (data?.formatter && data?.applyFormatterOn === "blur") {
        finalValue = applyFormatter(finalValue, data.formatter);
        setValue(finalValue);
        if (!data?.displayMask) {
          setDisplayValue(finalValue);
        } else {
          setDisplayValue(applyDisplayMask(finalValue, data.displayMask));
        }
      }

      // Apply built-in formatters
      if (data?.case) {
        finalValue = applyFormatter(finalValue, data.case);
        setValue(finalValue);
        setDisplayValue(finalValue);
      }

      if (data?.truncateMultipleSpaces) {
        finalValue = applyFormatter(finalValue, "truncateMultipleSpaces");
        setValue(finalValue);
        setDisplayValue(finalValue);
      }

      // Validate and only call onChange if valid (unless multiple textarea)
      if (!data?.multiple) {
        const validation = validateInput(finalValue);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);

        // Only call onChange if validation passes
        if (validation.isValid && finalValue !== value) {
          onChange?.(finalValue);
        }
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      onChange,
      value,
      validateInput,
    ]
  );

  const handleChange = useCallback(
    (e) => {
      let inputValue = e.target.value;
      let finalCleanValue = inputValue;
      let finalDisplayValue = inputValue;

      // Handle input vs display mask
      if (data?.inputMask && data?.applyMaskOn === "change") {
        const maskedValue = applyInputMask(inputValue, data.inputMask);
        finalCleanValue = maskedValue;
        finalDisplayValue = maskedValue;
      } else if (data?.displayMask && data?.applyMaskOn === "change") {
        finalDisplayValue = applyDisplayMask(inputValue, data.displayMask);
        finalCleanValue = inputValue;
      }

      // Apply custom formatter
      if (data?.formatter && data?.applyFormatterOn === "change") {
        finalCleanValue = applyFormatter(finalCleanValue, data.formatter);
        if (!data?.displayMask && !data?.inputMask) {
          finalDisplayValue = finalCleanValue;
        } else if (data?.displayMask) {
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        }
      }

      // Apply built-in formatters on change
      if (data?.case && data?.applyFormatterOn === "change") {
        finalCleanValue = applyFormatter(finalCleanValue, data.case);
        finalDisplayValue = finalCleanValue;
      }

      // Apply length restrictions
      if (
        data?.validate?.maxLength &&
        finalCleanValue.length > data.validate.maxLength
      ) {
        finalCleanValue = finalCleanValue.substring(0, data.validate.maxLength);
        if (data?.displayMask) {
          finalDisplayValue = applyDisplayMask(finalCleanValue, data.displayMask);
        } else {
          finalDisplayValue = finalCleanValue;
        }
      }

      // Update state with final values
      setValue(finalCleanValue);
      setDisplayValue(finalDisplayValue);

      // Auto-expand textarea
      setTimeout(adjustTextareaHeight, 0);

      // Validate input
      if (!data?.validateWhenHidden || !data?.hidden) {
        const validation = validateInput(finalCleanValue);
        setIsValid(validation.isValid);
        setErrorMessage(validation.errorMessage);

        // For non-multiple textareas, call onChange with the final clean value
        if (!data?.multiple && validation.isValid) {
          onChange?.(finalCleanValue);
        }
      } else if (!data?.multiple) {
        // Even if validation is skipped, still call onChange with clean value
        onChange?.(finalCleanValue);
      }
    },
    [
      data,
      applyInputMask,
      applyDisplayMask,
      applyFormatter,
      validateInput,
      onChange,
      adjustTextareaHeight,
    ]
  );

  // Handle Enter key for multiple textareas
  const handleKeyDown = useCallback(
    (e) => {
      if (data?.multiple && e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        handleMultipleAdd();
      }
    },
    [data?.multiple, handleMultipleAdd]
  );

  // Initialize component
  useEffect(() => {
    setIsValid(formValidation);

    if (data?.multiple && Array.isArray(initialValue)) {
      setMultipleValues(initialValue);
      setValue("");
      setDisplayValue("");
    } else {
      setValue(initialValue);
      // If display mask is present, apply it to initial value
      if (data?.displayMask) {
        setDisplayValue(applyDisplayMask(initialValue, data.displayMask));
      } else {
        setDisplayValue(initialValue);
      }
    }

    if (data?.autofocus && textareaRef.current) {
      textareaRef.current.focus();
    }

    // Set initial rows
    setCurrentRows(data?.rows || 3);
  }, [
    initialValue,
    formValidation,
    data?.autofocus,
    data?.multiple,
    data?.displayMask,
    data?.rows,
    applyDisplayMask,
  ]);

  // Update counts when value changes
  useEffect(() => {
    if (!data?.multiple) {
      updateCounts(value);
    }
  }, [value, updateCounts, data?.multiple]);

  // Auto-expand when value changes
  useEffect(() => {
    adjustTextareaHeight();
  }, [displayValue, adjustTextareaHeight]);

  // Focus edit textarea when editing starts
  useEffect(() => {
    if (editingIndex !== -1 && editTextareaRef.current) {
      editTextareaRef.current.focus();
      editTextareaRef.current.select();
    }
  }, [editingIndex]);

  // Don't render if hidden
  if (data?.hidden) {
    return null;
  }

  // Render label
  const renderLabel = () => {
    if (data?.hideLabel || !data?.label) return null;

    return (
      <div className="textarea__label" style={labelStyles.labelStyle}>
        {data?.labelPrefix && (
          <span className="textarea__label-prefix form-title">{data.labelPrefix}</span>
        )}
        <span className="form-title">{data.label}</span>
        {data?.validate?.required && (
          <span
            className={`textarea__required ${
              !isValid ? "textarea__required--error" : ""
            }`}
          >
            {" "}
            *
          </span>
        )}
        {data?.tooltip && (
          <span className="textarea__tooltip" title={data.tooltip}>
            ℹ️
          </span>
        )}
      </div>
    );
  };

  // Render multiple values display
  const renderMultipleValues = () => {
    if (!data?.multiple || multipleValues.length === 0) return null;

    return (
      <div className="textarea__multiple-values">
        {multipleValues.map((val, index) => (
          <div key={index} className="textarea__multiple-tag">
            {editingIndex === index ? (
              <div className="textarea__multiple-edit">
                <textarea
                  ref={editTextareaRef}
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  className="textarea__multiple-edit-input"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleMultipleSave(index);
                    } else if (e.key === "Escape") {
                      handleMultipleCancel();
                    }
                  }}
                />
                <div className="textarea__multiple-edit-actions">
                  <button
                    type="button"
                    onClick={() => handleMultipleSave(index)}
                    className="textarea__multiple-save"
                    aria-label="Save changes"
                    title="Save changes (Ctrl+Enter)"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="20,6 9,17 4,12"></polyline>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleMultipleCancel}
                    className="textarea__multiple-cancel"
                    aria-label="Cancel editing"
                    title="Cancel editing (Escape)"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <>
                <span
                  className="textarea__multiple-text"
                  onClick={() => handleMultipleEdit(index)}
                  title="Click to edit"
                >
                  {val.length > 100 ? `${val.substring(0, 100)}...` : val}
                </span>
                <button
                  type="button"
                  onClick={() => handleMultipleRemove(index)}
                  className="textarea__multiple-remove"
                  aria-label={`Remove entry`}
                  title={`Remove this entry`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render textarea
  const renderTextarea = () => {
    // Determine placeholder
    let placeholder = data?.placeholder;
    if (data?.multiple) {
      placeholder = placeholder || "Enter text (Ctrl+Enter to add)";
    }

    const textareaProps = {
      ref: textareaRef,
      disabled: data?.disabled || false,
      readOnly: data?.readOnly || false,
      value: displayValue,
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder,
      className: `textarea__input ${data?.customClass || ""}`,
      maxLength: data?.validate?.maxLength,
      minLength: data?.validate?.minLength,
      rows: data?.fixedSize ? data?.rows || 3 : currentRows,
      autoComplete: data?.autocomplete || "off",
      spellCheck: data?.spellcheck !== false,
      "aria-invalid": !isValid,
      "aria-describedby": !isValid ? `${data?.key}-error` : undefined,
      "data-input-mask": data?.inputMask,
      "data-display-mask": data?.displayMask,
      style: data?.autoExpand && !data?.fixedSize ? { resize: "none" } : {},
    };

    const textareaWrapperClasses = [
      "textarea__input-wrapper my-2",
      isFocused && "textarea__input-wrapper--focused",
      !isValid && "textarea__input-wrapper--error",
      data?.disabled && "textarea__input-wrapper--disabled",
      data?.readOnly && "textarea__input-wrapper--readonly",
      (data?.prefix || data?.suffix) && "textarea__input-wrapper--with-addons",
      data?.autoExpand && "textarea__input-wrapper--auto-expand",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="textarea__field">
        {renderMultipleValues()}

        <div className={textareaWrapperClasses}>
          {/* Prefix */}
          {data?.prefix && (
            <span className="textarea__prefix">{data.prefix}</span>
          )}

          {/* Textarea */}
          <textarea {...textareaProps} />

          {/* Suffix */}
          {data?.suffix && (
            <span className="textarea__suffix">{data.suffix}</span>
          )}
        </div>

        {/* Multiple textarea add button */}
        {data?.multiple && (
          <div className="textarea__multiple-controls">
            <button
              type="button"
              onClick={handleMultipleAdd}
              className="textarea__multiple-add"
              disabled={!value.trim()}
            >
              Add Entry
            </button>
            <span className="textarea__multiple-hint">
              Ctrl+Enter to add quickly
            </span>
          </div>
        )}

        {/* Error Messages */}
        {!isValid && (
          <div id={`${data?.key}-error`} className="textarea__error">
            {errorMessage}
          </div>
        )}

        {/* Word and Character Count */}
        {(data?.showWordCount || data?.showCharCount) && !data?.multiple && (
          <div className="textarea__counts">
            {data?.showWordCount && (
              <span className="textarea__count">
                Words: {wordCount}
                {data?.validate?.maxWords && `/${data.validate.maxWords}`}
              </span>
            )}
            {data?.showCharCount && (
              <span className="textarea__count">
                Characters: {charCount}
                {data?.validate?.maxLength && `/${data.validate.maxLength}`}
              </span>
            )}
          </div>
        )}

        {/* Auto-expand info */}
        {data?.autoExpand && !data?.fixedSize && (
          <div className="textarea__auto-expand-info">
            Textarea will expand automatically as you type
          </div>
        )}

        {/* Description */}
        {data?.description && (
          <div className="textarea__description">
            {isUrl(data.description) ? (
              <img
                src={data.description}
                alt="Field description"
                className="textarea__description-image"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.parentNode.textContent = data.description;
                }}
              />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: data.description }} />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`textarea ${formClass || ""} ${
        data?.disabled ? "textarea--disabled" : ""
      } ${data?.readOnly ? "textarea--readonly" : ""} ${
        data?.wysiwyg ? "textarea--wysiwyg" : ""
      } ${data?.autoExpand ? "textarea--auto-expand" : ""}`}
    >
      <div className="textarea__container" style={labelStyles.containerStyle}>
        {renderLabel()}
        {renderTextarea()}
      </div>
    </div>
  );
};

export default TextArea;